import { compact, find, map } from 'lodash';
import type { FC } from 'react';
import { useContext } from 'react';
import React from 'react';

import AddressBlock from '../components/review/AddressBlock';
import '../components/review/Review.scss';
import ReviewFooter from '../components/review/ReviewFooter';
import ReviewItem from '../components/review/ReviewItem';
import ReviewSection from '../components/review/ReviewSection';
import ConfigContext from '../service/context/config';
import useAppStore, { SERVICE_CARD } from '../service/store/app';
import useOrderStore from '../service/store/order';
import { scrollToRef } from '../utils/scroll';

interface IStepReviewProps {
  onSubmit: () => Promise<void>;
}

const SERVICE_LABELS: Record<string, string> = {
  [SERVICE_CARD.INSPEKTION]: 'Inspektion',
  [SERVICE_CARD.REPARATUR]: 'Reparatur',
  [SERVICE_CARD.ZUBEHOER]: 'Zubehör'
};

// Liste aus {key: anzahl} + Config-Definitionen zu "Name × n" auflösen.
const buildItemList = (
  counts: { [key: string]: number } | undefined,
  definitions: { key: string; name: string }[] | undefined
): { key: string; label: string }[] =>
  compact(map(counts, (count, key) => {
    if (!count || count < 1) {
      return undefined;
    }

    const definition = find(definitions, { key });

    if (!definition) {
      return undefined;
    }

    return { key, label: `${definition.name} × ${count}` };
  }));

const StepReview: FC<IStepReviewProps> = ({ onSubmit }) => {
  const config = useContext(ConfigContext);
  const setStep = useAppStore(state => state.setStep);
  const selectedServiceCard = useAppStore(state => state.selectedServiceCard);
  const onlyAccessoriesSelected = useAppStore(state => state.onlyAccessoriesSelected);
  const order = useOrderStore(state => state.order);

  const {
    product_name,
    shipping_reasons,
    shipping_reason_other,
    number_of_parcels,
    order_info,
    pickup_date,
    coupon,
    address,
    diffPickupAddress,
    pickupAddress,
    diffDeliveryAddress,
    deliveryAddress,
    diffInvoiceRecipient,
    invoiceRecipient,
    customer_number,
    reference_number,
    accessories,
    jaws
  } = order || {};

  const goToStep = (step: number) => (): void => {
    setStep(step);
    scrollToRef();
  };

  const handleSubmit = (): void => {
    void onSubmit();
    scrollToRef();
  };

  const pickupAddressToShow = diffPickupAddress && pickupAddress ? pickupAddress : address;
  const contactName = compact([ address?.salutation, address?.firstname, address?.lastname ]).join(' ');
  const parcelsLabel = number_of_parcels
    ? `${number_of_parcels} ${number_of_parcels === 1 ? 'Paket' : 'Pakete'}`
    : undefined;

  const accessoryList = buildItemList(accessories, config?.accessories);
  const jawsList = buildItemList(jaws, config?.jaws);
  const equipmentList = [ ...accessoryList, ...jawsList ];

  return (
    <div className="review">
      <ReviewSection
        icon="wrench"
        title="Service & Gerät"
        onEdit={goToStep(2)}
      >
        <ReviewItem
          label="Service"
          value={selectedServiceCard ? SERVICE_LABELS[selectedServiceCard] : undefined}
        />
        <ReviewItem
          label="Gerät"
          value={onlyAccessoriesSelected ? 'Nur Zubehör' : product_name}
        />
        {!onlyAccessoriesSelected && (
          <ReviewItem
            label="Grund der Einsendung"
            value={(shipping_reasons || []).length > 0 ? (
              <>
                {(shipping_reasons || []).map(reason => (
                  <div key={reason}>{reason}</div>
                ))}
              </>
            ) : undefined}
          />
        )}
        <ReviewItem label="Anderer Grund" value={shipping_reason_other} />
      </ReviewSection>

      {equipmentList.length > 0 && (
        <ReviewSection
          icon="package"
          title="Zubehör & Pressbacken"
          onEdit={goToStep(4)}
        >
          <ReviewItem
            label="Ausgewähltes Zubehör"
            full
            value={(
              <div className="review-products">
                {equipmentList.map(item => (
                  <div className="review-product" key={item.key}>
                    <span className="review-product-name">{item.label}</span>
                  </div>
                ))}
              </div>
            )}
          />
        </ReviewSection>
      )}

      <ReviewSection
        icon="package"
        title="Abholinformationen"
        onEdit={goToStep(5)}
      >
        <ReviewItem label="Abholdatum" value={pickup_date} />
        <ReviewItem label="Anzahl Pakete" value={parcelsLabel} />
        <ReviewItem label="Hinweise zur Abholung" value={order_info} />
        <ReviewItem
          label="Abweichende Abholadresse"
          value={diffPickupAddress ? 'Ja' : 'Nein'}
        />
        <ReviewItem
          label="Abholadresse"
          full
          value={<AddressBlock address={pickupAddressToShow} />}
        />
      </ReviewSection>

      <ReviewSection
        icon="users"
        title="Kundendaten"
        onEdit={goToStep(5)}
      >
        <ReviewItem label="Kundennummer" value={customer_number} />
        <ReviewItem label="Referenznummer" value={reference_number} />
        <ReviewItem label="Firma" value={address?.company} />
        <ReviewItem label="Ansprechpartner" value={contactName} />
        <ReviewItem label="E-Mail" value={address?.email} />
        <ReviewItem label="Telefon" value={address?.phone} />
        <ReviewItem label="Mobilnummer" value={address?.mobile} />
        <ReviewItem label="Gutscheincode" value={coupon} />
        <ReviewItem label="Land" value={address?.country} />
      </ReviewSection>

      {diffDeliveryAddress && deliveryAddress && (
        <ReviewSection
          icon="truck"
          title="Abweichende Rücksendeadresse"
          onEdit={goToStep(5)}
        >
          <ReviewItem
            label="Rücksendeadresse"
            full
            value={<AddressBlock address={deliveryAddress} />}
          />
        </ReviewSection>
      )}

      {diffInvoiceRecipient && invoiceRecipient && (
        <ReviewSection
          icon="file"
          title="Abweichender Rechnungsempfänger"
          onEdit={goToStep(5)}
        >
          <ReviewItem
            label="E-Mail Rechnungsempfänger"
            value={invoiceRecipient.email}
          />
          <ReviewItem
            label="Rechnungsempfänger"
            full
            value={<AddressBlock address={invoiceRecipient} />}
          />
        </ReviewSection>
      )}

      <ReviewFooter
        onSubmit={handleSubmit}
        submitText="Auftrag verbindlich erteilen"
      />
    </div>
  );
};

export default StepReview;
